import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import Pusher, { Channel } from 'pusher-js';

import { AuthService } from '../auth/services/auth.service';
import { EnvironmentService } from '../environment/environment.service';

export interface BrowserPushEvent {
  event_id: string;
  event_type: string;
  reference_id: string;
  title: string;
  body: string;
  payload?: { task_code?: string; workflow_code?: string } | null;
  branch_number?: string;
  damage_status?: string;
}

// Fired for every inbox notification regardless of the recipient's channel
// preferences (email/mobile/browser push) — this is what keeps the bell live
// even when browser_push is disabled or not configured for the event.
export interface InboxUpdateEvent {
  app_id: string;
  event_id: string;
}

@Injectable({ providedIn: 'root' })
export class PusherService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly envService = inject(EnvironmentService);
  private readonly ngZone = inject(NgZone);

  private pusher: Pusher | null = null;
  private channel: Channel | null = null;

  private readonly _notification$ = new Subject<BrowserPushEvent>();
  readonly notification$ = this._notification$.asObservable();

  private readonly _inboxUpdate$ = new Subject<InboxUpdateEvent>();
  readonly inboxUpdate$ = this._inboxUpdate$.asObservable();

  private readonly _connected$ = new BehaviorSubject<boolean>(false);
  readonly connected$ = this._connected$.asObservable();

  connect(userEmail: string): void {
    if (this.pusher) {
      return;
    }

    const { key, cluster, authEndpoint } = this.envService.pusherConfig;

    this.pusher = new Pusher(key, {
      cluster,
      channelAuthorization: {
        endpoint: authEndpoint,
        transport: 'ajax',
        customHandler: async (
          { socketId, channelName }: { socketId: string; channelName: string },
          callback: (error: Error | null, data: any) => void
        ) => {
          try {
            const token = this.authService.getAccessToken();
            const response = await fetch(authEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Authorization: `Bearer ${token}`,
              },
              body: new URLSearchParams({ socket_id: socketId, channel_name: channelName }),
            });

            if (!response.ok) {
              callback(new Error(`Pusher auth failed: ${response.status}`), null);
              return;
            }

            callback(null, await response.json());
          } catch (err) {
            callback(err as Error, null);
          }
        },
      },
    });

    this.pusher.connection.bind('state_change', ({ current }: { current: string }) => {
      if (current !== 'connected') {
        this.ngZone.run(() => this._connected$.next(false));
      }
    });

    const channelName = `private-repairhub-user-${userEmail.toLowerCase()}`;
    this.channel = this.pusher.subscribe(channelName);

    this.channel.bind('browser_push', (data: BrowserPushEvent) => {
      this.ngZone.run(() => this._notification$.next(data));
    });

    this.channel.bind('inbox', (data: InboxUpdateEvent) => {
      this.ngZone.run(() => this._inboxUpdate$.next(data));
    });

    this.channel.bind('pusher:subscription_succeeded', () => {
      this.ngZone.run(() => this._connected$.next(true));
    });

    this.channel.bind('pusher:subscription_error', (status: number) => {
      console.error(`[PusherService] subscription error on ${channelName}: status=${status}`);
      this.ngZone.run(() => this._connected$.next(false));
    });
  }

  disconnect(): void {
    if (this.channel) {
      this.channel.unbind_all();
      this.channel = null;
    }
    if (this.pusher) {
      this.pusher.disconnect();
      this.pusher = null;
    }
    this._connected$.next(false);
  }

  ngOnDestroy(): void {
    this.disconnect();
    this._notification$.complete();
    this._inboxUpdate$.complete();
    this._connected$.complete();
  }
}
